import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import PublishedWithChangesRoundedIcon from '@mui/icons-material/PublishedWithChangesRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import { TEMPLATE_UPLOAD_EVENT } from '../../services/templateUpload';

const formatBytes = (value) => {
  const bytes = Number(value) || 0;
  if (bytes <= 0) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const scaled = bytes / (1024 ** index);
  return `${scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} ${units[index]}`;
};

const formatElapsed = (ms) => {
  const totalSeconds = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const ACTIVE_PHASES = new Set(['uploading', 'processing']);

function StatTile({ icon, label, value, color = 'text.primary', highlight = false }) {
  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 110,
        border: 1,
        borderColor: highlight ? 'divider' : 'divider',
        borderRadius: 2,
        p: 1.25,
        textAlign: 'center',
        bgcolor: highlight ? 'action.hover' : 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', color, mb: 0.5 }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ lineHeight: 1.1, color, fontWeight: 700 }}>
        {Number.isFinite(value) ? value : 0}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {label}
      </Typography>
    </Box>
  );
}
export default function TemplateUploadProgressModal() {
  const [state, setState] = useState({
    open: false,
    phase: 'uploading', // uploading | processing | result | error
    entityLabel: 'Items',
    fileName: '',
    fileSize: 0,
    percent: 0,
    loaded: 0,
    total: 0,
    startedAt: 0,
    elapsedMs: 0,
    summary: null,
    errorMessage: '',
    uncertain: false
  });
  const tickRef = useRef(null);

  const stopTicker = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startTicker = useCallback((startedAt) => {
    stopTicker();
    const startedMs = Number(startedAt) || Date.now();
    tickRef.current = setInterval(() => {
      setState((prev) => ({ ...prev, elapsedMs: Math.max(0, Date.now() - startedMs) }));
    }, 1000);
    setState((prev) => ({ ...prev, startedAt: startedMs, elapsedMs: 0 }));
  }, [stopTicker]);

  useEffect(() => () => stopTicker(), [stopTicker]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleUploadEvent = (event) => {
      const detail = event?.detail || {};
      const type = detail.type;

      if (type === 'start') {
        startTicker(detail.startedAt);
        setState((prev) => ({
          ...prev,
          open: true,
          phase: 'uploading',
          entityLabel: detail.entityLabel || prev.entityLabel || 'Items',
          fileName: detail.fileName || '',
          fileSize: Number(detail.fileSize) || 0,
          percent: 0,
          loaded: 0,
          total: Number(detail.fileSize) || 0,
          summary: null,
          errorMessage: '',
          uncertain: false
        }));
        return;
      }

      if (type === 'progress') {
        const total = Number(detail.total) || 0;
        const loaded = Number(detail.loaded) || 0;
        const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
        setState((prev) => {
          if (!prev.open) return prev;
          return {
            ...prev,
            phase: prev.phase === 'result' || prev.phase === 'error' ? prev.phase : 'uploading',
            loaded,
            total,
            percent: Math.max(prev.percent, percent)
          };
        });
        return;
      }

      if (type === 'processing') {
        if (detail.startedAt) startTicker(detail.startedAt);
        setState((prev) => (prev.open ? { ...prev, phase: 'processing', percent: 100 } : prev));
        return;
      }

      if (type === 'result') {
        stopTicker();
        setState((prev) => ({
          ...prev,
          open: true,
          phase: 'result',
          percent: 100,
          startedAt: Number(detail.startedAt) || prev.startedAt || Date.now(),
          elapsedMs: Math.max(0, Date.now() - (Number(detail.startedAt) || prev.startedAt || Date.now())),
          summary: detail.summary || null
        }));
        return;
      }

      if (type === 'error') {
        stopTicker();
        setState((prev) => ({
          ...prev,
          open: true,
          phase: 'error',
          percent: 100,
          errorMessage: detail.message || 'Upload failed. Please try again.',
          uncertain: detail.uncertain === true,
          elapsedMs: Math.max(0, Date.now() - (Number(prev.startedAt) || Date.now()))
        }));
      }
    };

    window.addEventListener(TEMPLATE_UPLOAD_EVENT, handleUploadEvent);
    return () => window.removeEventListener(TEMPLATE_UPLOAD_EVENT, handleUploadEvent);
  }, [startTicker, stopTicker]);

  const handleClose = useCallback(() => {
    stopTicker();
    setState((prev) => ({ ...prev, open: false }));
  }, [stopTicker]);

  const handleRequestClose = useCallback((_event, reason) => {
    // The modal cannot be dismissed while the file is being uploaded/imported —
    // closing it mid-request would hide the operation and could look like a hang.
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    handleClose();
  }, [handleClose]);

  const { open, phase, entityLabel, fileName, fileSize, percent, loaded, total, elapsedMs, summary, errorMessage, uncertain } = state;
  const isBusy = ACTIVE_PHASES.has(phase);
  const summaryData = summary || { total: 0, created: 0, updated: 0, skipped: 0, errorCount: 0, issues: [], extra: {} };
  // Completeness: rows from the template that were created or updated.
  const importedOk = Number(summaryData.created) + Number(summaryData.updated);
  const completenessPercent = summaryData.total > 0
    ? Math.min(100, Math.round((importedOk / summaryData.total) * 100))
    : 100;
  const missingRows = Math.max(0, Number(summaryData.total) - importedOk);

  const renderBody = () => {
    if (phase === 'uploading') {
      const showPercent = percent > 0 && percent < 100;
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
          <UploadFileRoundedIcon color="primary" sx={{ fontSize: 44 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Uploading {entityLabel.toLowerCase()} file…
            </Typography>
            {fileName && (
              <Typography variant="body2" color="text.secondary">
                {fileName}
              </Typography>
            )}
          </Box>
          {showPercent ? (
            <>
              <LinearProgress variant="determinate" value={percent} sx={{ width: '100%' }} />
              <Typography variant="body2" color="text.secondary">
                {formatBytes(loaded)} of {formatBytes(total || fileSize)} · {percent}%
              </Typography>
            </>
          ) : (
            <>
              <LinearProgress sx={{ width: '100%' }} />
              <Typography variant="body2" color="text.secondary">
                Sending {formatBytes(fileSize)} to the server…
              </Typography>
            </>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <HourglassTopRoundedIcon sx={{ fontSize: 15 }} />
            Elapsed {formatElapsed(elapsedMs)}
          </Typography>
        </Stack>
      );
    }

    if (phase === 'processing') {
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
          <HourglassTopRoundedIcon color="primary" sx={{ fontSize: 44 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Importing {entityLabel.toLowerCase()} rows…
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fileName}
            </Typography>
          </Box>
          <LinearProgress sx={{ width: '100%' }} />
          <Alert severity="info" sx={{ width: '100%' }}>
            The file has been uploaded and the server is importing every row. Large
            files can take several minutes. Keep this window open — the import will
            continue until it finishes.
          </Alert>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Elapsed {formatElapsed(elapsedMs)}
          </Typography>
        </Stack>
      );
    }

    if (phase === 'error') {
      const isCertainFailure = !uncertain;
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
          <ErrorOutlineRoundedIcon color="error" sx={{ fontSize: 44 }} />
          <Alert severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
          {isCertainFailure ? (
            <Typography variant="body2" color="text.secondary">
              If this was a template problem, no rows were changed — download the latest template
              and try again. If the error happened mid-import, some rows may already have been saved,
              so refresh the list before re-uploading.
            </Typography>
          ) : (
            <Alert severity="warning" sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Your rows may have been saved
              </Typography>
              <Typography variant="body2">
                The server stopped replying before the import result could be returned (a gateway
                timeout / connection drop), but large imports usually keep running and finish in the
                background. Refresh the list to see whether your rows arrived. Re-uploading the same
                template is safe — matching rows are updated instead of duplicated — so check first,
                then try again if anything is missing.
              </Typography>
            </Alert>
          )}
        </Stack>
      );
    }

    // phase === 'result'
    const totalIssues = Number(summaryData.skipped) + Number(summaryData.errorCount);
    const issuePreview = (summaryData.issues || []).slice(0, 10);
    return (
      <Stack spacing={2} sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <CheckCircleOutlineRoundedIcon color="success" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {entityLabel} upload complete
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Processed in {formatElapsed(elapsedMs)}.
            </Typography>
          </Box>
        </Box>

        <Alert severity={missingRows > 0 ? 'warning' : 'success'} sx={{ width: '100%' }}>
          <Typography variant="body2">
            <strong>Completeness:</strong>{' '}
            {summaryData.total > 0
              ? `${importedOk} of ${summaryData.total} rows (${completenessPercent}%) in the template were imported or updated.`
              : 'No template rows were reported for this import.'}
            {missingRows > 0
              ? ` ${missingRows} row${missingRows === 1 ? '' : 's'} ${missingRows === 1 ? 'was' : 'were'} skipped / failed — see the issues below.`
              : (summaryData.total > 0 ? ' All template rows are accounted for.' : '')}
          </Typography>
        </Alert>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Status comparison</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <StatTile icon={<TableChartRoundedIcon fontSize="small" />} label="Total rows" value={summaryData.total} />
            <StatTile
              icon={<CloudDoneRoundedIcon fontSize="small" />}
              label="Uploaded (created)"
              value={summaryData.created}
              color="success.main"
              highlight={summaryData.created > 0}
            />
            <StatTile
              icon={<PublishedWithChangesRoundedIcon fontSize="small" />}
              label="Updated"
              value={summaryData.updated}
              color="info.main"
              highlight={summaryData.updated > 0}
            />
            <StatTile
              icon={<SkipNextRoundedIcon fontSize="small" />}
              label="Skipped / failed"
              value={totalIssues}
              color="warning.main"
              highlight={totalIssues > 0}
            />
            <StatTile
              icon={<ReportProblemRoundedIcon fontSize="small" />}
              label="Errors"
              value={summaryData.errorCount}
              color="error.main"
              highlight={summaryData.errorCount > 0}
            />
          </Box>
          {summaryData.extra && (
            (Number(summaryData.extra.categoriesCreated) > 0
              || Number(summaryData.extra.uomsCreated) > 0
              || Number(summaryData.extra.adjustmentsCreated) > 0) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Also created: {[
                  summaryData.extra.categoriesCreated > 0 ? `${summaryData.extra.categoriesCreated} categories` : '',
                  summaryData.extra.uomsCreated > 0 ? `${summaryData.extra.uomsCreated} UOMs` : '',
                  summaryData.extra.adjustmentsCreated > 0 ? `${summaryData.extra.adjustmentsCreated} stock adjustments` : ''
                ].filter(Boolean).join(' · ')}
              </Typography>
            )
          )}
        </Box>

        {issuePreview.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Row issues {summaryData.errorCount > issuePreview.length ? `(first ${issuePreview.length} of ${summaryData.errorCount})` : ''}
            </Typography>
            <Box sx={{ maxHeight: 180, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'action.hover' }}>
              {issuePreview.map((issue, index) => (
                <Typography key={`issue-${index}`} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  • {issue}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Stack>
    );
  };

  const titleText = {
    uploading: `Upload ${entityLabel} Template`,
    processing: `Importing ${entityLabel}…`,
    result: 'Upload Complete',
    error: `${entityLabel} Upload Failed`
  }[phase] || 'Template Upload';

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2 } }}
      aria-labelledby="template-upload-progress-title"
    >
      <DialogTitle id="template-upload-progress-title">{titleText}</DialogTitle>
      <DialogContent dividers={phase === 'result' || phase === 'error' || isBusy}>
        {renderBody()}
      </DialogContent>
      {(phase === 'result' || phase === 'error') && (
        <DialogActions>
          <Button onClick={handleClose} variant={phase === 'error' ? 'outlined' : 'contained'} autoFocus>
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

